"use client";

import { type RefObject, useEffect, useRef, useState } from "react";

type MediaEl = HTMLVideoElement | HTMLAudioElement;
type VideoFrameEl = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: () => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};
type SeekableEl = MediaEl & { fastSeek?: (time: number) => void };

/**
 * Move the playhead. `approximate` uses `fastSeek` (nearest keyframe — much cheaper,
 * for live scrubbing) where the browser supports it (Firefox/Safari; Chrome lacks
 * it), falling back to an exact `currentTime=` seek otherwise and for final seeks.
 */
function applySeek(el: MediaEl, time: number, approximate: boolean): void {
  const v = el as SeekableEl;
  if (approximate && typeof v.fastSeek === "function") v.fastSeek(time);
  else el.currentTime = time;
}

/**
 * Drives loop playback of the [start, end] selection on the preview element:
 * a per-frame loop (via `requestVideoFrameCallback`, rAF fallback) snaps the
 * playhead back to `start` when it reaches `end`. Exposes the live `current`
 * time, whether it's `playing`, and `seek` / `playClip` helpers.
 *
 * Reads the live selection through `stateRef` so the frame loop never re-subscribes.
 */
export function useLoopPlayback({
  getPreviewElement,
  previewUrl,
  duration,
  stateRef,
  isAudio = false,
}: {
  getPreviewElement?: () => MediaEl | null;
  previewUrl: string | null;
  duration: number;
  stateRef: RefObject<{ start: number; end: number; loop: boolean }>;
  /**
   * Audio-only media plays through a `<video>` element that has
   * `requestVideoFrameCallback` but never presents frames, so rVFC never fires
   * and the playhead would freeze. Force the rAF loop in that case.
   */
  isAudio?: boolean;
}) {
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  // Latest seek target while a seek is still in flight (null = none pending). Lets
  // rapid scrubbing keep just one decode in flight instead of thrashing the decoder.
  const pendingSeek = useRef<{ t: number; approximate: boolean } | null>(null);

  const seek = (time: number, approximate = false) => {
    const el = getPreviewElement?.();
    if (!el) return;
    const t = Math.max(0, Math.min(time, duration));
    // A new seek while the previous one hasn't finished aborts and restarts it.
    // Coalesce: stash the latest target and apply it on the next 'seeked'.
    if (el.seeking) {
      pendingSeek.current = { t, approximate };
      return;
    }
    pendingSeek.current = null;
    applySeek(el, t, approximate);
    setCurrent(t);
  };

  const pause = () => {
    getPreviewElement?.()?.pause();
  };

  const playClip = () => {
    const el = getPreviewElement?.();
    if (!el) return;
    if (el.paused) {
      const { start, end } = stateRef.current;
      if (el.currentTime < start || el.currentTime >= end - 0.012) el.currentTime = start;
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  };

  useEffect(() => {
    const el = getPreviewElement?.();
    if (!el) return;
    const video = el as VideoFrameEl;
    const useRvfc = !isAudio && typeof video.requestVideoFrameCallback === "function";
    let active = true;
    let handle = 0;

    const onFrame = () => {
      if (!active) return;
      const { start, end, loop } = stateRef.current;
      setCurrent(el.currentTime);
      if (loop && !el.paused && (el.currentTime >= end - 0.012 || el.currentTime < start - 0.25)) {
        el.currentTime = start;
      }
      handle = useRvfc
        ? (video.requestVideoFrameCallback?.(onFrame) ?? 0)
        : requestAnimationFrame(onFrame);
    };
    handle = useRvfc
      ? (video.requestVideoFrameCallback?.(onFrame) ?? 0)
      : requestAnimationFrame(onFrame);

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    // When the in-flight seek finishes, apply the latest pending target (if any) —
    // this is what keeps a fast scrub to one decode at a time, latest wins.
    const onSeeked = () => {
      const next = pendingSeek.current;
      if (!next) return;
      pendingSeek.current = null;
      applySeek(el, next.t, next.approximate);
      setCurrent(next.t);
    };
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("seeked", onSeeked);

    return () => {
      active = false;
      if (useRvfc) video.cancelVideoFrameCallback?.(handle);
      else cancelAnimationFrame(handle);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("seeked", onSeeked);
    };
  }, [getPreviewElement, previewUrl, stateRef, isAudio]);

  return { current, playing, seek, playClip, pause };
}
